import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { Tooltip as RechartsTooltip } from 'recharts';
import {
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { adminApi } from '../services/api';
import { useVisibilityRefresh } from '../hooks/useVisibilityRefresh';

interface MessagingData {
  totalMessages: number;
  activeConversations: number;
  messagesPerDay: Array<{ _id: string; count: number }>;
  activeMessagers: number;
}

interface MessagingAnalyticsProps {
  isVisible?: boolean;
}

const MessagingAnalytics: React.FC<MessagingAnalyticsProps> = ({ isVisible = true }) => {
  const [data, setData] = useState<MessagingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminApi.getMessagingAnalytics();
      setData(result);
    } catch (err: any) {
      setError('Mesajlaşma verileri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Use visibility refresh hook
  const { lastRefresh, manualRefresh } = useVisibilityRefresh(fetchData, {
    refreshInterval: 45000, // 45 seconds
    isVisible,
    enabled: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const formatXAxisLabel = (tickItem: string) => {
    const [, month, day] = tickItem.split('-');
    return `${day}/${month}`;
  };

  if (loading) {
    return (
      <Box sx={{ height: 420, borderRadius: 2, border: '1px solid', borderColor: 'grey.200', p: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <CircularProgress size={40} thickness={4} />
        </Box>
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ height: 420, borderRadius: 2, border: '1px solid', borderColor: 'grey.200', p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error || 'Veri yüklenemedi'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with refresh info */}
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={3}
        sx={{ px: { xs: 1, sm: 2 } }}
      >
        <Typography variant="h4" sx={{ 
          fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' },
          fontWeight: 600,
          color: 'text.primary'
        }}>
          Mesajlaşma Analitikleri
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            {format(lastRefresh, 'HH:mm')}
          </Typography>
          <Tooltip title="Yenile">
            <IconButton 
              onClick={manualRefresh} 
              disabled={loading}
              sx={{ 
                p: 0.5,
                borderRadius: 1,
                backgroundColor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.200',
                '&:hover': {
                  backgroundColor: 'grey.100'
                }
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Summary Stats - Single Row */}
      <Box sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'grey.200',
        backgroundColor: 'white',
        p: 2.5,
        mb: 3
      }}>
        <Box 
          display="grid" 
          gridTemplateColumns={{ 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: 'repeat(4, 1fr)'
          }} 
          gap={3}
          sx={{ alignItems: 'start' }}
        >
          {/* Total Messages */}
          <Box sx={{ 
            textAlign: 'center',
            minHeight: 100,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
              {data.totalMessages.toLocaleString()}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              Toplam Mesaj
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              Son 30 günde
            </Typography>
          </Box>

          {/* Active Conversations */}
          <Box sx={{ 
            textAlign: 'center',
            minHeight: 100,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main', mb: 0.5 }}>
              {data.activeConversations.toLocaleString()}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              Aktif Konuşma
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              Devam eden sohbetler
            </Typography>
          </Box>

          {/* Active Messagers */}
          <Box sx={{ 
            textAlign: 'center',
            minHeight: 100,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main', mb: 0.5 }}>
              {data.activeMessagers.toLocaleString()}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              Mesaj Gönderen
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              Aktif kullanıcılar
            </Typography>
          </Box>

          {/* Daily Average */}
          <Box sx={{ 
            textAlign: 'center',
            minHeight: 100,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'secondary.main', mb: 0.5 }}>
              {Math.round(data.totalMessages / 30)}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              Günlük Ortalama
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              mesaj/gün
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Chart */}
      <Box sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'grey.200',
        backgroundColor: 'white',
        p: 2.5
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
          Son 7 Günde Mesaj Aktivitesi
        </Typography>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data.messagesPerDay}>
            <CartesianGrid strokeDasharray="1 1" stroke="#f0f0f0" />
            <XAxis 
              dataKey="_id" 
              tickFormatter={formatXAxisLabel}
              fontSize={11}
              stroke="#666"
            />
            <YAxis fontSize={11} stroke="#666" />
            <RechartsTooltip 
              labelFormatter={(label: any) => `Tarih: ${formatXAxisLabel(label)}`}
              formatter={(value: any) => [`${value} mesaj`, 'Mesaj Sayısı']}
              contentStyle={{
                borderRadius: 8,
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}
            />
            <Bar 
              dataKey="count" 
              fill="#667eea" 
              radius={[2, 2, 0, 0]}
              stroke="#667eea"
              strokeWidth={0.5}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default MessagingAnalytics;

